import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { GitCompareArrows, RotateCcw, X } from "lucide-react";
import { Shutter } from "../shutter/Shutter";
import { HistoryDiff } from "./HistoryDiff";
import { HistoryTimeline, HistoryTimelineSkeleton } from "./HistoryTimeline";
import { DeletedRecordList } from "./DeletedRecordList";
import { Theme } from "../../../theme";
import { formatHistoryDate } from "../../../utils/dateUtils";
import { useLanguageStore } from "../../../core/store/languageStore";
import { useAdminPermissions } from "../../../core/hooks/useAdminPermissions";
import { useConfirmModal } from "../../../core/hooks/useConfirmModal";
import {
  authorLabel,
  diffSnapshots,
  type DeletedRecord,
  type HistoryCollection,
  type HistorySnapshot,
  type RecordLabels,
} from "../../../core/history/entities/HistoryVersion";
import { getHistoryVersionsUseCase } from "../../../core/history/useCases/getHistoryVersionsUseCase";
import { getHistoryVersionUseCase } from "../../../core/history/useCases/getHistoryVersionUseCase";
import { getDeletedChildrenUseCase } from "../../../core/history/useCases/getDeletedChildrenUseCase";
import { restoreHistoryVersionUseCase } from "../../../core/history/useCases/restoreHistoryVersionUseCase";

/// Collections dont la descendance supprimée est consultable, alignées sur `CHILD_RELATIONS`
/// dans `pocketbase/pb_hooks/services/adminAuthService.js`.
const COLLECTIONS_WITH_CHILDREN: HistoryCollection[] = ["topics", "topicParts"];

/// Terme de droite de la comparaison.
///
/// - `result` — l'état résultant de l'événement sélectionné : ce que cet événement a changé ;
/// - `current` — l'état actuel de l'enregistrement, réglage **persistant** : on parcourt la
///   timeline en gardant la même référence, ce qui est la question réelle (« qu'est-ce qui a changé
///   depuis ? ») ;
/// - `pinned` — une seconde version épinglée depuis la timeline, qui l'emporte sur le réglage
///   ci-dessus : deux versions désignées explicitement sont une intention sans ambiguïté.
type CompareMode = "result" | "current" | "pinned";

type PanelTab = "versions" | "deleted";

/// Nombre d'éléments encore absents dans un arbre de suppressions, descendance comprise.
function countPending(records: DeletedRecord[]): number {
  return records.reduce(
    (total, record) =>
      total +
      (record.status === "deleted" ? 1 : 0) +
      countPending(record.children),
    0,
  );
}

interface HistoryPanelProps {
  collection: HistoryCollection;
  recordId: string;
  /// Titre de l'enregistrement, affiché en sous-titre du panneau.
  recordLabel?: string;
  onClose: () => void;
  /// Appelé après une restauration réussie, pour rafraîchir la page appelante.
  onRestored?: () => void;
}

export function HistoryPanel({
  collection,
  recordId,
  recordLabel,
  onClose,
  onRestored,
}: HistoryPanelProps) {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const permissions = useAdminPermissions();
  const { showModal, ConfirmModalComponent } = useConfirmModal();

  const [tab, setTab] = useState<PanelTab>("versions");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [compareCurrent, setCompareCurrent] = useState(false);
  const [showReorders, setShowReorders] = useState(false);

  const supportsChildren = COLLECTIONS_WITH_CHILDREN.includes(collection);

  const timelineQuery = useQuery({
    queryKey: ["history", collection, recordId],
    queryFn: () => getHistoryVersionsUseCase.exec(collection, recordId),
  });

  const deletedQuery = useQuery({
    queryKey: ["history-deleted", collection, recordId],
    queryFn: () => getDeletedChildrenUseCase.exec(collection, recordId),
    enabled: supportsChildren,
  });

  const allVersions = useMemo(
    () => timelineQuery.data?.versions ?? [],
    [timelineQuery.data],
  );
  const current = timelineQuery.data?.current ?? null;

  // Un glisser-déposer génère une ligne par rang modifié : laissées visibles par défaut, elles
  // noieraient les modifications de contenu, qui sont l'objet du panneau.
  const reorderCount = useMemo(
    () => allVersions.filter((version) => version.action === "reorder").length,
    [allVersions],
  );

  const versions = useMemo(
    () =>
      showReorders
        ? allVersions
        : allVersions.filter((version) => version.action !== "reorder"),
    [allVersions, showReorders],
  );

  // La liste visible change de longueur avec le filtre : une sélection hors bornes n'aurait plus
  // de correspondance.
  useEffect(() => {
    setSelectedIndex((index) => (index < versions.length ? index : 0));
  }, [versions.length]);

  const selected = versions[selectedIndex];

  const pinnedIndex = useMemo(
    () => (pinnedId ? versions.findIndex((item) => item.id === pinnedId) : -1),
    [pinnedId, versions],
  );

  // Le filtre des déplacements peut retirer de la liste la version épinglée : la garder épinglée
  // afficherait une comparaison avec un terme invisible.
  useEffect(() => {
    if (pinnedId && pinnedIndex < 0) {
      setPinnedId(null);
    }
  }, [pinnedId, pinnedIndex]);

  const pinned = pinnedIndex >= 0 ? versions[pinnedIndex] : null;

  const mode: CompareMode =
    pinned && pinned.id !== selected?.id
      ? "pinned"
      : compareCurrent
        ? "current"
        : "result";

  /// Les deux termes de la comparaison, ordonnés chronologiquement.
  ///
  /// La timeline va du plus récent au plus ancien : un index plus grand désigne une version plus
  /// ancienne. C'est ce qui place le bon terme à gauche quand l'épinglé précède le sélectionné.
  const sides = useMemo(() => {
    if (!selected) {
      return { leftId: null, rightId: null, rightIsCurrent: false };
    }

    if (mode === "pinned" && pinned) {
      const selectedIsOlder = selectedIndex > pinnedIndex;
      return {
        leftId: selectedIsOlder ? selected.id : pinned.id,
        rightId: selectedIsOlder ? pinned.id : selected.id,
        rightIsCurrent: false,
      };
    }

    if (mode === "current") {
      return { leftId: selected.id, rightId: null, rightIsCurrent: true };
    }

    // Résultat de l'événement sélectionné : la version immédiatement plus récente, ou l'état
    // actuel s'il n'y en a pas. Une suppression, elle, n'a pas d'état résultant.
    if (selected.action === "delete") {
      return { leftId: selected.id, rightId: null, rightIsCurrent: false };
    }

    const next = versions[selectedIndex - 1];
    return {
      leftId: selected.id,
      rightId: next ? next.id : null,
      rightIsCurrent: !next,
    };
  }, [mode, pinned, pinnedIndex, selected, selectedIndex, versions]);

  const leftSnapshotQuery = useQuery({
    queryKey: ["history-snapshot", sides.leftId],
    queryFn: () => getHistoryVersionUseCase.exec(sides.leftId!),
    enabled: !!sides.leftId,
  });

  const rightSnapshotQuery = useQuery({
    queryKey: ["history-snapshot", sides.rightId],
    queryFn: () => getHistoryVersionUseCase.exec(sides.rightId!),
    enabled: !!sides.rightId,
  });

  const leftSnapshot = leftSnapshotQuery.data?.snapshot ?? null;

  const rightSnapshot: HistorySnapshot | null = useMemo(() => {
    if (sides.rightId) return rightSnapshotQuery.data?.snapshot ?? null;
    return sides.rightIsCurrent ? current : null;
  }, [sides, rightSnapshotQuery.data, current]);

  const diffs = useMemo(
    () => diffSnapshots(leftSnapshot, rightSnapshot),
    [leftSnapshot, rightSnapshot],
  );

  // Chacune des requêtes ne résout que les ids de son propre snapshot : les trois se complètent,
  // et l'état courant (porté par la timeline) est l'un des termes possibles de la comparaison.
  const labels = useMemo<RecordLabels>(
    () => ({
      ...(timelineQuery.data?.labels ?? {}),
      ...(leftSnapshotQuery.data?.labels ?? {}),
      ...(rightSnapshotQuery.data?.labels ?? {}),
    }),
    [timelineQuery.data, leftSnapshotQuery.data, rightSnapshotQuery.data],
  );

  // Une apparition n'a pas d'« avant » et une suppression pas d'« après » : les présenter en
  // avant/après afficherait une colonne systématiquement vide.
  const layout = useMemo<"change" | "single">(() => {
    if (mode !== "result") return "change";
    if (selected?.appearance || selected?.action === "delete") return "single";
    return "change";
  }, [mode, selected]);

  const singleSide = selected?.appearance ? "after" : "before";

  /// Phrase de contexte du détail. Dérivée de l'état affiché, et non de l'action brute : une
  /// `update` dont l'état d'avant serait vide tomberait sinon sur une clé inexistante.
  const detailKey = useMemo(() => {
    if (layout !== "single") return "change";
    if (selected?.action === "delete") return "delete";
    return selected?.action === "restore" ? "restore" : "create";
  }, [layout, selected]);

  /// Libellé d'un terme de la comparaison, par son id de version.
  function sideLabel(versionId: string | null, isCurrent: boolean): string {
    if (isCurrent) return t("history.currentState");
    const version = versions.find((item) => item.id === versionId);
    return version ? formatHistoryDate(version.created, language) : "";
  }

  const leftLabel = sideLabel(sides.leftId, false);
  const rightLabel = sideLabel(sides.rightId, sides.rightIsCurrent);

  /// La restauration ajoute un événement en tête de timeline : les index se décalent, on repart
  /// donc de la version la plus récente plutôt que de conserver une sélection devenue trompeuse.
  ///
  /// Le réglage « comparer à l'état actuel » survit, lui : il exprime une préférence de lecture,
  /// pas une sélection.
  function resetSelection() {
    setSelectedIndex(0);
    setPinnedId(null);
  }

  function handleRestoreVersion() {
    if (!selected) return;

    showModal(
      t("history.confirmRestoreTitle"),
      t("history.confirmRestoreMessage"),
      async () => {
        const result = await restoreHistoryVersionUseCase.exec(selected.id);
        if (result) {
          resetSelection();
          await timelineQuery.refetch();
          onRestored?.();
        }
      },
    );
  }

  function handleRestoreDeleted(record: DeletedRecord) {
    // La recréation est en cascade : annoncer la descendance ramenée avec l'élément, plutôt que de
    // la faire découvrir après coup.
    const childCount = countPending(record.children);

    showModal(
      t("history.confirmRestoreDeletedTitle"),
      childCount > 0
        ? t("history.confirmRestoreDeletedWithChildrenMessage", {
            label: record.label,
            count: childCount,
          })
        : t("history.confirmRestoreDeletedMessage", { label: record.label }),
      async () => {
        const result = await restoreHistoryVersionUseCase.exec(
          record.versionId,
        );
        if (result) {
          // La recréation trace un événement `restore` : la timeline change aussi, et l'élément
          // supprimé doit repasser en « restauré ».
          await Promise.all([deletedQuery.refetch(), timelineQuery.refetch()]);
          onRestored?.();
        }
      },
    );
  }

  // Mémoïsé : le repli `?? []` créerait un nouveau tableau à chaque rendu.
  const deleted = useMemo(() => deletedQuery.data ?? [], [deletedQuery.data]);

  // Le compteur de l'onglet annonce ce qui manque encore : les éléments déjà restaurés restent
  // listés comme trace, mais n'appellent plus d'action.
  const deletedCount = useMemo(() => countPending(deleted), [deleted]);

  const isLoading = timelineQuery.isLoading;
  const isSnapshotLoading =
    leftSnapshotQuery.isLoading || rightSnapshotQuery.isLoading;

  const tabs: { id: PanelTab; label: string; count?: number }[] = [
    {
      id: "versions",
      label: t("history.tab.versions"),
      count: versions.length,
    },
    ...(supportsChildren
      ? [
          {
            id: "deleted" as PanelTab,
            label: t("history.tab.deleted"),
            count: deletedCount,
          },
        ]
      : []),
  ];

  return (
    <>
      <Shutter
        onClose={onClose}
        closeLabel={t("common.closePanel")}
        resizeAriaLabel={t("common.resizePanel")}
        initialWidthPercent={72}
        minWidthPercent={35}
        contentClassName="flex min-h-0 flex-1 flex-col"
      >
        <header className="border-b border-gray-200 px-4 pt-4 dark:border-gray-700 md:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t("history.title")}
              </h2>
              {!!recordLabel && (
                <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                  {recordLabel}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.closePanel")}
              className="-mt-1 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <X size={18} />
            </button>
          </div>

          <nav
            className="-mb-px mt-3 flex gap-1"
            aria-label={t("history.title")}
          >
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                aria-current={tab === item.id}
                className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  tab === item.id
                    ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                    : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {item.label}
                {item.count !== undefined && (
                  <span className="ms-1.5 text-xs tabular-nums text-gray-400 dark:text-gray-500">
                    {item.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6">
          {tab === "versions" ? (
            isLoading ? (
              <HistoryTimelineSkeleton />
            ) : allVersions.length === 0 ? (
              <EmptyState
                title={t("history.empty")}
                description={t("history.emptyHint")}
              />
            ) : (
              <div className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
                <div className="space-y-3">
                  <HistoryTimeline
                    collection={collection}
                    versions={versions}
                    selectedIndex={selectedIndex}
                    onSelect={(index) => {
                      setSelectedIndex(index);
                      // Une version ne se compare pas à elle-même : sélectionner la version
                      // épinglée la désépingle, sinon l'épingle resterait latente et son bouton,
                      // masqué sur la ligne sélectionnée, hors d'atteinte.
                      setPinnedId((current) =>
                        current === versions[index]?.id ? null : current,
                      );
                    }}
                    pinnedId={pinnedId}
                    onTogglePin={(id) =>
                      setPinnedId((current) => (current === id ? null : id))
                    }
                    language={language}
                  />

                  {reorderCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowReorders((value) => !value)}
                      className="w-full rounded-lg px-2.5 py-1.5 text-left text-xs text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    >
                      {showReorders
                        ? t("history.hideReorders")
                        : t("history.showReorders", { count: reorderCount })}
                    </button>
                  )}
                </div>

                <div className="min-w-0 space-y-4">
                  {selected && (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      {mode === "pinned" ? (
                        <PinnedComparison
                          before={leftLabel}
                          after={rightLabel}
                          clearLabel={t("history.clearComparison")}
                          onClear={() => setPinnedId(null)}
                        />
                      ) : (
                        <CompareCurrentToggle
                          checked={compareCurrent}
                          onChange={setCompareCurrent}
                          label={t("history.compareCurrentToggle")}
                        />
                      )}

                      {permissions.canEdit && (
                        <button
                          type="button"
                          onClick={handleRestoreVersion}
                          className="flex items-center gap-2 rounded-3xl border border-gray-300 px-3 py-1.5 text-sm text-gray-900 transition-colors hover:bg-blue-600 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-blue-600"
                        >
                          <RotateCcw className="h-4 w-4" aria-hidden="true" />
                          {/* Deux versions comparées : laquelle « cet état » désigne n'est plus
                              évident, la sélectionnée pouvant se trouver dans l'une ou l'autre
                              colonne selon la chronologie. On la nomme. */}
                          {mode === "pinned"
                            ? t("history.restoreVersionOf", {
                                date: formatHistoryDate(
                                  selected.created,
                                  language,
                                ),
                              })
                            : t("history.restoreThisState")}
                        </button>
                      )}
                    </div>
                  )}

                  {selected && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t(`history.detail.${detailKey}`, {
                        author: authorLabel(
                          selected.author,
                          t("history.unknownAuthor"),
                        ),
                        date: formatHistoryDate(selected.created, language),
                      })}
                    </p>
                  )}

                  {isSnapshotLoading ? (
                    <DetailSkeleton />
                  ) : (
                    <HistoryDiff
                      collection={collection}
                      diffs={diffs}
                      labels={labels}
                      layout={layout}
                      singleSide={singleSide}
                      beforeLabel={leftLabel}
                      afterLabel={rightLabel}
                      emptyMessage={t("history.noDifference")}
                    />
                  )}
                </div>
              </div>
            )
          ) : deletedQuery.isLoading ? (
            <HistoryTimelineSkeleton />
          ) : deleted.length === 0 ? (
            <EmptyState
              title={t("history.noDeletedItems")}
              description={t("history.noDeletedItemsHint")}
            />
          ) : (
            <DeletedRecordList
              records={deleted}
              canRestore={permissions.role === "admin"}
              onRestore={handleRestoreDeleted}
            />
          )}
        </div>

        <footer className="border-t border-gray-200 px-4 py-2.5 dark:border-gray-700 md:px-6">
          <p className="text-xs leading-relaxed text-gray-400 dark:text-gray-500">
            {t("history.limitations")}
          </p>
        </footer>
      </Shutter>
      <ConfirmModalComponent />
    </>
  );
}

/// Réglage persistant : comparer la version examinée à l'état actuel de l'enregistrement.
///
/// Reprend le vocabulaire du `Switch` des formulaires (piste bleue active, pastille blanche), en
/// gabarit de barre d'outils — le composant de formulaire impose son propre libellé « Oui / Non ».
function CompareCurrentToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="group flex items-center gap-2.5 rounded-lg px-1 py-1 text-sm text-gray-600 transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:text-gray-100"
    >
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-[1.125rem]" : "translate-x-1"
          }`}
        />
      </span>
      {label}
    </button>
  );
}

/// Deux versions désignées explicitement : l'état du comparateur, et le moyen d'en sortir.
function PinnedComparison({
  before,
  after,
  clearLabel,
  onClear,
}: {
  before: string;
  after: string;
  clearLabel: string;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs text-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
      <GitCompareArrows className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="tabular-nums">
        {before}
        <span className="mx-1.5 text-blue-400 dark:text-blue-500">→</span>
        {after}
      </span>
      <button
        type="button"
        onClick={onClear}
        aria-label={clearLabel}
        title={clearLabel}
        className="-me-1 rounded p-0.5 text-blue-500 transition-colors hover:bg-blue-100 hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400 dark:hover:bg-blue-900/50 dark:hover:text-blue-100"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className={`${Theme.greyBorder} px-6 py-10 text-center`}>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {title}
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
        {description}
      </p>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      {[0, 1].map((row) => (
        <div key={row} className="space-y-1.5">
          <div className="h-3 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="grid gap-3 md:grid-cols-2">
            <div className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
            <div className="h-16 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ))}
    </div>
  );
}
